'use client';

import { useTheme } from 'next-themes';
import { useCallback } from 'react';
import { flushSync } from 'react-dom';

const TRANSITION_DURATION = 400;

type Theme = 'light' | 'dark';

interface UseThemeTransitionResult {
  resolvedTheme: string | undefined;
  setThemeAnimated: (next: Theme) => Promise<void>;
  toggleThemeAnimated: () => Promise<void>;
}

/**
 * The wipe always opens from the top-right corner of the viewport — the corner
 * the theme control lives in — no matter which toggle triggered it. Anchoring
 * it to the clicked element instead made the origin wander (the settings-page
 * toggle sits top-centre), and a toggle inside a menu that unmounts mid-swap
 * reported an all-zero rect, sending the circle to the opposite corner.
 */
function getWipeGeometry() {
  const x = window.innerWidth;
  const y = 0;

  return {
    x,
    y,
    // Furthest corner from the origin: the opposite (bottom-left) one.
    maxRadius: Math.hypot(x, window.innerHeight - y),
  };
}

export function useThemeTransition(): UseThemeTransitionResult {
  const { setTheme, resolvedTheme } = useTheme();

  const setThemeAnimated = useCallback(
    async (next: Theme) => {
      if (resolvedTheme === next) return;

      const supportsViewTransition =
        typeof document !== 'undefined' && typeof document.startViewTransition === 'function';

      if (!supportsViewTransition) {
        setTheme(next);
        return;
      }

      // Read before the swap: the transition freezes rendering and can move or
      // unmount the control that triggered it.
      const { x, y, maxRadius } = getWipeGeometry();

      // Published for the CSS fallback in globals.css. Chrome overrides it with
      // the script animation below; engines without `pseudoElement` support in
      // Element.animate() run the CSS one with the exact same geometry.
      const root = document.documentElement;
      root.style.setProperty('--theme-wipe-x', `${x}px`);
      root.style.setProperty('--theme-wipe-y', `${y}px`);
      root.style.setProperty('--theme-wipe-r', `${maxRadius}px`);
      root.style.setProperty('--theme-wipe-duration', `${TRANSITION_DURATION}ms`);
      root.dataset.themeWipe = '';

      const transition = document.startViewTransition(() => {
        flushSync(() => {
          setTheme(next);
        });
      });
      transition.finished
        .catch(() => {})
        .finally(() => {
          delete root.dataset.themeWipe;
        });

      try {
        await transition.ready;
      } catch {
        return; // transition skipped (superseded/hidden tab) — theme is already set
      }

      const wipe = root.animate(
        {
          clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`],
        },
        {
          duration: TRANSITION_DURATION,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        },
      );

      // The snapshot + style recalc can freeze rendering for hundreds of ms on
      // heavy pages. If the clock started now, the first frame the user sees
      // would already be a half-grown circle mid-screen. Hold the wipe at 0px
      // (old theme stays on screen) until the compositor presents a frame,
      // then run the full animation.
      wipe.pause();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      wipe.play();
    },
    [resolvedTheme, setTheme],
  );

  const toggleThemeAnimated = useCallback(
    () => setThemeAnimated(resolvedTheme === 'dark' ? 'light' : 'dark'),
    [resolvedTheme, setThemeAnimated],
  );

  return { resolvedTheme, setThemeAnimated, toggleThemeAnimated };
}
